import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import Typography from '@alltrails/core/components/Typography';
import useUser from '@alltrails/context/hooks/useUser';
import usePlans from '@alltrails/context/hooks/usePlans';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import Plans from '@alltrails/core/types/Plans';
import type Experiments from '@alltrails/core/types/Experiments';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import type SubscriptionUser from '@alltrails/types/SubscriptionUser';
import PeakFeatureList from '../PeakFeatureList/PeakFeatureList';
import PlanSelection from '../PlanSelection/PlanSelection';
import styles from './UpsellContent.module.scss';
import TierSubheading from './TierSubheading';

type Props = {
  tierSelected: PurchaseTier;
  setTierSelected: (tier: PurchaseTier) => void;
  upsellTrigger: UpsellTrigger;
  promoEligibleHeading: React.ReactNode;
  heading: React.ReactNode;
  showPlusFirst?: boolean;
  contentVariant?: NonNullable<Experiments['web-growth-rm-single-dual-tier-post-auth-upsell']>['value'];
  subscriptionUser: SubscriptionUser | null;
};

const SingleTierContent = ({
  isPromoEligible,
  promoEligibleHeading,
  heading,
  tier
}: {
  isPromoEligible: boolean;
  promoEligibleHeading: React.ReactNode;
  heading: React.ReactNode;
  tier: PurchaseTier;
}) => {
  return (
    <>
      {tier === 'peak' ? (
        <PeakLogoBadge size="md" variant="full" className={styles.logo} />
      ) : (
        <PlusLogoBadge size="md" variant="full" className={styles.logo} />
      )}

      <Typography variant="heading400">{isPromoEligible ? promoEligibleHeading : heading}</Typography>
      <TierSubheading tier={tier} />
    </>
  );
};

const DualTierContent = ({
  isPromoEligible,
  promoEligibleHeading,
  heading,
  tierSelected,
  setTierSelected,
  showPlusFirst,
  upsellTrigger,
  plans,
  subscriptionUser
}: {
  isPromoEligible: boolean;
  promoEligibleHeading: React.ReactNode;
  heading: React.ReactNode;
  tierSelected: PurchaseTier;
  setTierSelected: (tier: PurchaseTier) => void;
  showPlusFirst: boolean;
  upsellTrigger: UpsellTrigger;
  plans: Plans;
  subscriptionUser: SubscriptionUser | null;
}) => {
  return (
    <>
      <Typography variant="heading400">{isPromoEligible ? promoEligibleHeading : heading}</Typography>
      <PlanSelection
        isPromoEligible={isPromoEligible}
        plans={plans}
        tierSelected={tierSelected}
        setTierSelected={setTierSelected}
        showPlusFirst={showPlusFirst}
        trigger={upsellTrigger}
        subscriptionUser={subscriptionUser}
      />
    </>
  );
};

const GenericUpsellContent = ({
  tierSelected,
  setTierSelected,
  upsellTrigger,
  promoEligibleHeading,
  heading,
  showPlusFirst,
  contentVariant,
  subscriptionUser
}: Props) => {
  const user = useUser();
  const plans = usePlans();
  const isPromoEligible = !user || user?.isPromoEligible;
  const isPlus = user?.subscriptionTier === 'plus';

  return (
    <>
      {isPlus || contentVariant === 'peak_only' ? (
        <SingleTierContent tier="peak" isPromoEligible={isPromoEligible} promoEligibleHeading={promoEligibleHeading} heading={heading} />
      ) : contentVariant === 'plus_only' ? (
        <SingleTierContent tier="plus" isPromoEligible={isPromoEligible} promoEligibleHeading={promoEligibleHeading} heading={heading} />
      ) : (
        <DualTierContent
          showPlusFirst={showPlusFirst || contentVariant === 'plus_default'}
          upsellTrigger={upsellTrigger}
          plans={plans}
          isPromoEligible={isPromoEligible}
          promoEligibleHeading={promoEligibleHeading}
          heading={heading}
          tierSelected={tierSelected}
          setTierSelected={setTierSelected}
          subscriptionUser={subscriptionUser}
        />
      )}
      <PeakFeatureList tierSelected={tierSelected} upsellTrigger={upsellTrigger} hidePeak={contentVariant === 'plus_only'} />
    </>
  );
};

export default GenericUpsellContent;
